'use client';

import { useEffect } from 'react';
import type { Logger } from '@core/be/platform/logging/Logger';

export interface UseKeyboardMindmapControlsOptions {
  editingId: string | null;
  renameLogger: Logger;
  editingInputRef: React.MutableRefObject<HTMLInputElement | HTMLTextAreaElement | null>;
  cancelEditRef: React.MutableRefObject<boolean>;
  editingSelectionModeRef: React.MutableRefObject<'select-all' | 'caret-end'>;
  setPendingEditText: (value: string | null) => void;
  setEditingId: (id: string | null) => void;
  primarySelected: string | null;
  isDebouncedKey: (code: string, delay?: number) => boolean;
  handleCreateSibling: (before: boolean) => void;
  handleCreateChild: (first: boolean) => void;
  handleDeleteSelection: () => void;
  handleMoveSelection: (direction: 'left' | 'right' | 'up' | 'down') => void;
  applySelection: (ids: string[]) => void;
  // undo/redo
  undo: () => void;
  redo: () => void;
  // global Tab fallback
  canvasRef: React.MutableRefObject<HTMLDivElement | null>;
  focusCanvas: () => void;
  selectedRawIdsRef: React.MutableRefObject<string[]>;
}

export interface UseKeyboardMindmapControlsResult {
  handleNodeKeyDown: (e: React.KeyboardEvent<HTMLDivElement>) => void;
}

export function useKeyboardMindmapControls({
  editingId,
  renameLogger,
  editingInputRef,
  cancelEditRef,
  editingSelectionModeRef,
  setPendingEditText,
  setEditingId,
  primarySelected,
  isDebouncedKey,
  handleCreateSibling,
  handleCreateChild,
  handleDeleteSelection,
  handleMoveSelection,
  applySelection,
  undo,
  redo,
  canvasRef,
  focusCanvas,
  selectedRawIdsRef,
}: UseKeyboardMindmapControlsOptions): UseKeyboardMindmapControlsResult {
  const handleNodeKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (!editingId && (e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'z') {
      e.preventDefault();
      e.stopPropagation();
      if (e.shiftKey) redo();
      else undo();
      return;
    }
    if (!editingId && (e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'y') {
      e.preventDefault();
      e.stopPropagation();
      redo();
      return;
    }

    renameLogger.debug(
      'canvas keydown: key=%s editing=%s target=%s repeat=%s meta=%s ctrl=%s alt=%s primary=%s',
      e.key,
      editingId ?? 'null',
      (e.target as HTMLElement | null)?.tagName ?? 'unknown',
      e.repeat,
      e.metaKey,
      e.ctrlKey,
      e.altKey,
      primarySelected ?? 'null'
    );

    if (editingId) {
      if (editingInputRef.current && document.activeElement !== editingInputRef.current) {
        renameLogger.debug('canvas keydown redirecting focus back to editor input');
        editingInputRef.current.focus();
        try {
          const el = editingInputRef.current;
          if (el) {
            const end = el.value.length;
            el.setSelectionRange(end, end);
          }
        } catch {}
      }
      renameLogger.debug('canvas keydown ignored because editing is active');
      // Prevent bubbling to global handlers while editing in local scope
      e.stopPropagation();
      return;
    }

    if (e.key.length === 1 && !e.metaKey && !e.ctrlKey && !e.altKey && primarySelected) {
      e.preventDefault();
      e.stopPropagation();
      cancelEditRef.current = false;
      renameLogger.debug('inline edit start from typing: key=%s selected=%s', e.key, primarySelected);
      editingSelectionModeRef.current = 'caret-end';
      setPendingEditText(e.key);
      setEditingId(primarySelected);
      return;
    }

    switch (e.key) {
      case 'F2': {
        if (!primarySelected) return;
        e.preventDefault();
        e.stopPropagation();
        cancelEditRef.current = false;
        renameLogger.debug('F2 rename start for %s', primarySelected);
        editingSelectionModeRef.current = 'select-all';
        setEditingId(primarySelected);
        setPendingEditText(null);
        applySelection([primarySelected]);
        return;
      }
      case 'Tab': {
        if (e.repeat) {
          e.preventDefault();
          e.stopPropagation();
          return;
        }
        const keyCode = `Tab:${e.shiftKey ? '1' : '0'}`;
        if (isDebouncedKey(keyCode)) {
          e.preventDefault();
          e.stopPropagation();
          return;
        }
        e.preventDefault();
        e.stopPropagation();
        if (!primarySelected) return;
        handleCreateChild(e.shiftKey);
        return;
      }
      case 'Enter': {
        if (e.repeat) {
          e.preventDefault();
          e.stopPropagation();
          return;
        }
        const keyCode = `Enter:${e.shiftKey ? '1' : '0'}`;
        if (isDebouncedKey(keyCode)) {
          e.preventDefault();
          e.stopPropagation();
          return;
        }
        e.preventDefault();
        e.stopPropagation();
        if (!primarySelected) return;
        handleCreateSibling(e.shiftKey);
        return;
      }
      case 'Delete':
      case 'Backspace': {
        e.preventDefault();
        e.stopPropagation();
        handleDeleteSelection();
        return;
      }
      case 'ArrowLeft': {
        e.preventDefault();
        e.stopPropagation();
        handleMoveSelection('left');
        return;
      }
      case 'ArrowRight': {
        e.preventDefault();
        e.stopPropagation();
        handleMoveSelection('right');
        return;
      }
      case 'ArrowUp': {
        e.preventDefault();
        e.stopPropagation();
        handleMoveSelection('up');
        return;
      }
      case 'ArrowDown': {
        e.preventDefault();
        e.stopPropagation();
        handleMoveSelection('down');
        return;
      }
      default:
        break;
    }
  };

  // Global Tab handler when canvas isn’t focused
  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      if (editingId) return;
      if (event.key !== 'Tab' || event.metaKey || event.ctrlKey || event.altKey) return;
      const active = document.activeElement as HTMLElement | null;
      const isOnCanvas = active === canvasRef.current || !!active?.closest('.react-flow__pane');
      if (!isOnCanvas) {
        const keyCode = `Tab:${event.shiftKey ? '1' : '0'}`;
        if (isDebouncedKey(keyCode)) {
          event.preventDefault();
          return;
        }
        event.preventDefault();
        handleCreateChild(event.shiftKey);
        focusCanvas();
      }
    };
    window.addEventListener('keydown', handler, true);
    return () => window.removeEventListener('keydown', handler, true);
  }, [canvasRef, editingId, focusCanvas, handleCreateChild, isDebouncedKey]);

  return { handleNodeKeyDown };
}

export default useKeyboardMindmapControls;
